import type { ChangeEvent } from 'react';
import { useCallback } from 'react';

/**
 * Format phone number for input display with progressive formatting
 * Shows formatting as user types (e.g., "(123" for 3 digits)
 * @param value - Raw phone number (may include + for international)
 * @returns Formatted phone number string
 */
function formatPhoneNumberForInput(value: string): string {
  // Check if it starts with + (international)
  const hasPlus = value.startsWith('+');

  // Get the digits part (after the + if present)
  const digitsOnly = hasPlus
    ? value.substring(1).replace(/\D/g, '')
    : value.replace(/\D/g, '');

  // For US numbers starting with +1, format progressively
  if (hasPlus && digitsOnly.startsWith('1')) {
    const usDigits = digitsOnly.substring(1); // Remove country code '1'

    if (usDigits.length === 0) {
      return '+1';
    }

    // Format the US portion progressively
    if (usDigits.length <= 3) {
      return `+1 (${usDigits}`;
    }

    if (usDigits.length <= 6) {
      return `+1 (${usDigits.slice(0, 3)}) ${usDigits.slice(3)}`;
    }

    // For 7-10 digits, show with dash
    return `+1 (${usDigits.slice(0, 3)}) ${usDigits.slice(3, 6)}-${usDigits.slice(6, 10)}`;
  }

  // For other international numbers, just show + and digits (no complex formatting during input)
  if (hasPlus) {
    return `+${digitsOnly}`;
  }

  // For US numbers (no +), format progressively as (XXX) XXX-XXXX
  const limited = digitsOnly.slice(0, 10);

  // Apply formatting based on length
  if (limited.length === 0) {
    return '';
  }

  if (limited.length <= 3) {
    return `(${limited}`;
  }

  if (limited.length <= 6) {
    return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  }

  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
}

/**
 * Custom hook for formatting phone input as (XXX) XXX-XXXX
 * @param value - Current phone value (raw digits)
 * @param onChange - Callback when value changes
 * @returns Object with formatted value and change handler
 */
export function usePhoneInput(
  value: string,
  onChange: (value: string) => void,
) {
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const cursorPosition = input.selectionStart ?? 0;
      // Get the current formatted value from what's actually displayed
      const oldValue = input.value;
      const rawInput = e.target.value;

      // Check if this is an international number (starts with +)
      // Check both the raw input and the current stored value
      const isInternational = rawInput.startsWith('+') || value.startsWith('+');

      // Get the cleaned value - preserve + for international numbers
      let newNumbers: string;

      if (isInternational) {
        // For international: keep + and digits only, allow more digits
        const digitsOnly = rawInput
          .replace(/[^\d+]/g, '')
          .replace(/^\+\+/, '+');
        newNumbers = digitsOnly.startsWith('+')
          ? digitsOnly.slice(0, 20)
          : `+${digitsOnly.slice(0, 19)}`;
      } else {
        // For US numbers: digits only, max 10
        newNumbers = rawInput.replace(/\D/g, '').slice(0, 10);
      }

      // Check if user is deleting
      const isDeleting = rawInput.length < oldValue.length;

      // Update the value through the onChange callback
      onChange(newNumbers);

      // Format the new value for cursor positioning
      const formatted = formatPhoneNumberForInput(newNumbers);

      // Calculate new cursor position
      setTimeout(() => {
        if (!input) {
          return;
        }

        let newCursorPos = cursorPosition;

        // For international numbers, cursor positioning is simpler (no formatting chars except +)
        if (isInternational) {
          // Just position after the entered content
          newCursorPos = Math.min(formatted.length, cursorPosition);
        } else if (!isDeleting) {
          // When adding characters, advance cursor past formatting characters
          const digitsBeforeCursor = rawInput
            .slice(0, cursorPosition)
            .replace(/\D/g, '').length;

          // Find position in formatted string that has the same number of digits before it
          let digitCount = 0;

          for (let i = 0; i < formatted.length; i++) {
            if (/\d/.test(formatted[i] ?? '')) {
              digitCount++;

              if (digitCount === digitsBeforeCursor) {
                newCursorPos = i + 1;
                break;
              }
            }
          }

          // Skip over formatting characters
          while (
            newCursorPos < formatted.length &&
            /\D/.test(formatted[newCursorPos] ?? '')
          ) {
            newCursorPos++;
          }
        } else {
          // When deleting, we need to determine if it's backspace or delete key
          // Get the number of digits before the cursor in the raw input
          const digitsBeforeCursorInInput = rawInput
            .slice(0, cursorPosition)
            .replace(/\D/g, '').length;
          const oldDigitsBeforeCursor = oldValue
            .slice(0, cursorPosition)
            .replace(/\D/g, '').length;

          // If the number of digits before cursor decreased, it was a backspace
          // If it stayed the same, it was a delete key
          const isBackspace = digitsBeforeCursorInInput < oldDigitsBeforeCursor;

          if (isBackspace) {
            // Backspace: deleted character before cursor
            // Find the position in the formatted string
            let digitCount = 0;

            for (let i = 0; i < formatted.length; i++) {
              if (/\d/.test(formatted[i] ?? '')) {
                digitCount++;

                if (digitCount === digitsBeforeCursorInInput) {
                  newCursorPos = i + 1;
                  break;
                }
              }
            }

            // If we end up at the beginning and there are no digits, position at start
            if (digitsBeforeCursorInInput === 0) {
              newCursorPos = formatted.length > 0 ? 1 : 0; // After the opening paren if it exists
            }
          } else {
            // Delete key: deleted character at/after cursor
            // Cursor should stay at the same logical position relative to digits
            let digitCount = 0;

            for (let i = 0; i < formatted.length; i++) {
              if (/\d/.test(formatted[i] ?? '')) {
                if (digitCount === digitsBeforeCursorInInput) {
                  newCursorPos = i;
                  break;
                }
                digitCount++;
              }
            }

            // If cursor is after all digits, position at the end
            if (digitsBeforeCursorInInput >= newNumbers.length) {
              newCursorPos = formatted.length;
            } else if (
              digitsBeforeCursorInInput === 0 &&
              formatted.length > 0
            ) {
              newCursorPos = 1; // After opening paren
            }

            // Skip over any formatting characters at the new position
            while (
              newCursorPos < formatted.length &&
              /\D/.test(formatted[newCursorPos] ?? '')
            ) {
              newCursorPos++;
            }
          }
        }

        // Ensure cursor doesn't go past the end
        newCursorPos = Math.min(newCursorPos, formatted.length);

        input.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    },
    [value, onChange],
  );

  return {
    value: formatPhoneNumberForInput(value),
    onChange: handleChange,
    rawValue: value,
  };
}
